import { useEffect, useState } from "react";
import { api, apiBase } from "../api";

const GENERAL_FIELDS: { key: string; label: string; hint?: string }[] = [
  { key: "min_llm_score", label: "Min LLM score", hint: "Jobs scoring below this are dropped during harvest." },
  { key: "llm_extract", label: "Use LLM extraction", hint: "'true' normalizes listings with the model; 'false' uses keyword fallback." },
  { key: "sources_enabled", label: "Enabled sources", hint: "Comma list: remotive, remoteok, adzuna, themuse, greenhouse, lever, ashby, linkedin, indeed." },
];

const EMAIL_FIELDS: { key: string; label: string; hint?: string }[] = [
  { key: "sender_name", label: "Sender name", hint: "Name shown in the From line." },
  { key: "mailing_address", label: "Mailing address", hint: "Required for CAN-SPAM compliance; placed in the email footer." },
  { key: "max_emails_per_day", label: "Max emails per day", hint: "Throttle cap for the Gmail sender." },
  { key: "approval_required", label: "Approval required", hint: "'true' keeps every email in the human approval queue." },
];

const GMAIL_OAUTH_FIELDS: { key: string; label: string; hint?: string }[] = [
  { key: "gmail_client_id", label: "Gmail Client ID", hint: "OAuth client ID from Google Cloud Console." },
  { key: "gmail_client_secret", label: "Gmail Client Secret", hint: "OAuth client secret from Google Cloud Console." },
  { key: "gmail_user_id", label: "Gmail User ID", hint: "Your Gmail address (or 'me' for primary)." },
  { key: "gmail_service_account_file", label: "Service Account JSON", hint: "Path to service account JSON (Workspace only)." },
  { key: "gmail_delegated_user", label: "Delegated User", hint: "Email to impersonate (Workspace domain-wide delegation)." },
];

const TELEGRAM_FIELDS: { key: string; label: string; hint?: string }[] = [
  { key: "telegram_bot_token", label: "Bot token", hint: "From @BotFather. Used by `jobhunter telegram` on the agent runner." },
  { key: "telegram_allowed_users", label: "Allowed user ids", hint: "Comma-separated Telegram user ids that may control the bot. Empty = anyone (not recommended)." },
  { key: "telegram_draft_limit", label: "Draft limit", hint: "How many drafts are posted for approval per command." },
];

const PROVIDER_KEYS: { key: string; label: string; provider: "gemini" | "openai" | "anthropic" | "custom" | "hunter"; hint?: string }[] = [
  { key: "key_gemini", label: "Gemini API key", provider: "gemini", hint: "Generative AI Studio (aistudio.google.com)." },
  { key: "key_openai", label: "OpenAI API key", provider: "openai", hint: "platform.openai.com." },
  { key: "key_anthropic", label: "Anthropic API key", provider: "anthropic", hint: "console.anthropic.com." },
  { key: "key_custom_base", label: "Custom endpoint URL", provider: "custom", hint: "OpenAI-compatible base URL (Ollama, vLLM, Workers AI, ...)." },
  { key: "key_custom", label: "Custom API key", provider: "custom", hint: "Optional; only if the custom endpoint requires one." },
  { key: "key_hunter", label: "Hunter.io API key", provider: "hunter", hint: "Optional; used to find decision-maker emails." },
];

const LLM_TASKS = [
  "job_extraction",
  "job_classification",
  "company_profiling",
  "email_finding",
  "email_drafting",
  "email_personalization",
  "reply_analysis",
  "telegram_intent",
];

const PROVIDER_MODELS: Record<string, string[]> = {
  gemini: [
    "gemini-2.5-flash",
    "gemini-2.5-pro",
    "gemini-1.5-flash",
    "gemini-1.5-pro",
  ],
  openai: [
    "gpt-4o",
    "gpt-4o-mini",
    "gpt-4-turbo",
    "gpt-4",
    "gpt-3.5-turbo",
  ],
  anthropic: [
    "claude-3-5-sonnet-20241022",
    "claude-3-5-haiku-20241022",
    "claude-3-opus-20240229",
    "claude-3-sonnet-20240229",
    "claude-3-haiku-20240307",
  ],
  custom: [],
};

export default function Settings() {
  const [values, setValues] = useState<Record<string, string>>({});
  const [keys, setKeys] = useState<Record<string, string>>({});
  const [routing, setRouting] = useState<string>("");
  const [routingModels, setRoutingModels] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<"idle" | "saved" | "error">("idle");
  const [message, setMessage] = useState("");
  const [fetchingModels, setFetchingModels] = useState<string | null>(null);
  const [modelLists, setModelLists] = useState<Record<string, string[]>>({});

  useEffect(() => {
    api
      .settings()
      .then((s) => {
        setValues(s);
        setRouting(s.llm_routing ?? "{}");
        try {
          const parsed = JSON.parse(s.llm_routing ?? "{}");
          const models: Record<string, string> = {};
          for (const [task, cfg] of Object.entries(parsed)) {
            if (typeof cfg === "object" && cfg !== null && "model" in cfg) {
              models[task] = String(cfg.model);
            }
          }
          setRoutingModels(models);
        } catch {}
      })
      .catch((e) => flash("error", e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchModelsForProvider(provider: string) {
    const keyField = provider === "gemini" ? "key_gemini"
      : provider === "openai" ? "key_openai"
      : provider === "anthropic" ? "key_anthropic"
      : provider === "custom" ? "key_custom"
      : null;
    const baseField = provider === "custom" ? "key_custom_base" : null;

    const apiKey = keys[keyField!] || values[keyField!] || "";
    const baseUrl = baseField ? (keys[baseField] || values[baseField] || "") : "";

    if (!apiKey) {
      flash("error", `No API key for ${provider}`);
      return;
    }

    setFetchingModels(provider);
    try {
      const resp = await api.fetchModels({ provider: provider as any, api_key: apiKey, base_url: baseUrl || undefined });
      if (resp.error) {
        flash("error", resp.error);
      } else {
        setModelLists((prev) => ({ ...prev, [provider]: resp.models }));
        flash("saved", `Fetched ${resp.models.length} models for ${provider}`);
      }
    } catch (e) {
      flash("error", (e as Error).message);
    } finally {
      setFetchingModels(null);
    }
  }

  async function save() {
    try {
      JSON.parse(routing); // validate
      const typed = Object.fromEntries(Object.entries(keys).filter(([, v]) => v.trim() !== ""));
      const payload: Record<string, string> = { ...values, llm_routing: routing, ...typed };
      await api.saveSettings(payload);
      setKeys({});
      flash("saved", "Settings saved to D1.");
    } catch (e) {
      flash("error", e instanceof SyntaxError ? "llm_routing is not valid JSON." : (e as Error).message);
    }
  }

  function flash(kind: "saved" | "error", msg: string) {
    setStatus(kind);
    setMessage(msg);
    setTimeout(() => setStatus("idle"), 4000);
  }

  return (
    <div>
      <h2>Settings</h2>
      <p className="muted">
        Stored in the D1 <code>settings</code> table and served by the Worker API. Provider keys can be
        set here or as Cloudflare Secrets / <code>.env</code> (secrets win). Keys are never returned
        in full — the UI shows a masked placeholder and you only overwrite by typing a new value.
      </p>
      {status !== "idle" && <p className={status === "saved" ? "ok" : "err"}>{message}</p>}

      <div className="card">
        <h3>Provider keys</h3>
        <p className="muted">
          The worker's LLM proxy uses these when the matching Cloudflare secret or env var isn't set.
          Stored encrypted-at-rest in D1, never shown in full. Click "Fetch models" to discover available models.
        </p>
        {PROVIDER_KEYS.map((f) => (
          <div key={f.key} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
            <label style={{ flex: 1, minWidth: 200 }}>{f.label}{f.hint && <span className="muted"> — {f.hint}</span>}</label>
            <input
              type="password"
              value={keys[f.key] ?? ""}
              placeholder={values[f.key] ? "••••••" + values[f.key].slice(-4) : "not set"}
              onChange={(e) => setKeys({ ...keys, [f.key]: e.target.value })}
              autoComplete="off"
              style={{ flex: 2, minWidth: 250 }}
            />
            {f.provider !== "hunter" && (
              <button
                className="secondary"
                onClick={() => fetchModelsForProvider(f.provider)}
                disabled={fetchingModels === f.provider}
                style={{ height: 36 }}
              >
                {fetchingModels === f.provider ? "Fetching…" : "Fetch models"}
              </button>
            )}
            {modelLists[f.provider] && modelLists[f.provider].length > 0 && (
              <span className="muted" style={{ fontSize: 12 }}>
                {modelLists[f.provider].length} models available
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="card">
        <h3>Email</h3>
        <p className="muted">
          Used by the agent runner's Gmail sender. Set up once locally with{" "}
          <code>jobhunter gmail-setup</code> (creates <code>credentials.json</code> from Google Cloud and
          saves <code>token.json</code> after OAuth consent). Credentials never leave the runner.
        </p>
        {EMAIL_FIELDS.map((f) => (
          <div key={f.key}>
            <label>{f.label}{f.hint && <span className="muted"> — {f.hint}</span>}</label>
            <input value={values[f.key] ?? ""} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} />
          </div>
        ))}
        <h4 style={{ marginTop: 16 }}>Gmail OAuth (optional — for dashboard config)</h4>
        <p className="muted">
          Store OAuth credentials in D1 so the agent runner can fetch them via <code>keys-sync</code>.
          <code>credentials.json</code> and <code>token.json</code> still live on the runner.
        </p>
        {GMAIL_OAUTH_FIELDS.map((f) => (
          <div key={f.key}>
            <label>{f.label}{f.hint && <span className="muted"> — {f.hint}</span>}</label>
            <input value={values[f.key] ?? ""} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} />
          </div>
        ))}
      </div>

      <div className="card">
        <h3>General</h3>
        {GENERAL_FIELDS.map((f) => (
          <div key={f.key}>
            <label>{f.label}{f.hint && <span className="muted"> — {f.hint}</span>}</label>
            <input value={values[f.key] ?? ""} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} />
          </div>
        ))}
      </div>

      <div className="card">
        <h3>Telegram</h3>
        <p className="muted">
          The AI bot long-polls on the agent runner. Token + allowed ids here override{" "}
          <code>TELEGRAM_BOT_TOKEN</code>/<code>TELEGRAM_ALLOWED_USER_IDS</code> env vars when unset.
        </p>
        {TELEGRAM_FIELDS.map((f) => (
          <div key={f.key}>
            <label>{f.label}{f.hint && <span className="muted"> — {f.hint}</span>}</label>
            <input value={values[f.key] ?? ""} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} />
          </div>
        ))}
      </div>

      <div className="card">
        <h3>LLM routing (per task)</h3>
        <p className="muted">
          Each pipeline task maps to a provider + model: <code>gemini</code>, <code>openai</code>,{" "}
          <code>anthropic</code>, or <code>custom</code> (OpenAI-compatible endpoint). JSON shape:{" "}
          <code>{"{task: {provider, model?, fallback?}}"}</code>.
        </p>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
            Quick model picker (updates JSON below):
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8 }}>
            {LLM_TASKS.map((task) => {
              return (
                <div key={task} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ width: 140, fontWeight: 500 }}>{task}</span>
                  <select
                    value={routingModels[task] || ""}
                    onChange={(e) => setRoutingModels((prev) => ({ ...prev, [task]: e.target.value }))}
                    style={{ flex: 1 }}
                  >
                    <option value="">— model —</option>
                    {Object.entries(PROVIDER_MODELS).flatMap(([provider, models]) =>
                      models.map((m) => <option key={`${provider}:${m}`} value={m}>{provider}: {m}</option>)
                    )}
                    <option value="custom">Custom (edit JSON)</option>
                  </select>
                </div>
              );
            })}
          </div>
        </div>
        <textarea
          rows={12}
          value={routing}
          onChange={(e) => setRouting(e.target.value)}
          style={{ fontFamily: "monospace", fontSize: 12, width: "100%" }}
        />
        <div className="muted" style={{ marginTop: 8 }}>
          Available tasks: {LLM_TASKS.join(", ")}.
        </div>
      </div>

      <div className="card">
        <h3>Storage</h3>
        <p className="muted">
          Backend: <b>{values.storage_backend ?? "cloudflare"}</b> &middot; API base: <code>{apiBase()}</code>
        </p>
        <p className="muted">
          The agent runner selects its backend via <code>JOBHUNTER_STORAGE</code> (local SQLite or{" "}
          <code>cloudflare</code> via this API). D1 is the canonical store for the dashboard and worker.
        </p>
      </div>

      <div className="form-actions">
        <button className="primary" onClick={save}>Save all</button>
      </div>
    </div>
  );
}